import { useEffect, useState } from "react";
import { apiV2 } from "api/firebase";
import type {
  IListEmailReviewAllTasksItem,
  IListEmailReviewAllTasksRes,
} from "@evennt-tech/api-v2-external";

type Result = [
  IListEmailReviewAllTasksRes<Date> | undefined,
  boolean,
  { error?: unknown }
];

// Parse the row's two date columns from ISO strings to Date instances.
const parseRow = (
  row: IListEmailReviewAllTasksItem<string>
): IListEmailReviewAllTasksItem<Date> => ({
  ...row,
  lastActivityAt: new Date(row.lastActivityAt),
  dueDate: row.dueDate ? new Date(row.dueDate) : null,
});

/**
 * Anonymous "All Reviews" page loader. The magic-link `key` from the URL
 * is enough — the BE returns every task instance the reviewer was invited
 * to on that show, plus the reviewer's email, in the payload.
 */
export default function useReviewAllShowsTasks(
  key: string | null,
  includeDone: boolean
): Result {
  const [data, setData] = useState<IListEmailReviewAllTasksRes<Date>>();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<unknown>();

  useEffect(() => {
    if (!key) {
      setData(undefined);
      return;
    }
    let cancelled = false;
    setIsLoading(true);
    setError(undefined);
    apiV2.task
      .listEmailReviewAllTasks({ key, includeDone })
      .then((res) => {
        if (cancelled) return;
        setData({ ...res, items: res.items.map(parseRow) });
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err);
      })
      .finally(() => {
        if (cancelled) return;
        setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [key, includeDone]);

  return [data, isLoading, { error }];
}
